// Symmetric merge of two ABC-statistics periods for the comparison view (CW-2615).
//
// The report fetches each period with a separate single-period query, then joins
// them on the client. The join must be a full outer join keyed on the active
// grouping's match keys: the resulting item set has to be complete and identical
// regardless of which period is chosen as primary, and each period's measures
// must stay in its own columns. An item that sold in only one period keeps its
// identity fields (so it is still listed) but must NOT borrow the other period's
// absent measures — otherwise a comparison-only item would show a bogus ABC
// class / revenue for a period it never sold in.

// Item-identity / dimension fields shared by both periods. These are safe to
// populate in the base columns for a comparison-only item. Everything else
// (measures, ABC class, ranks, sale times) is period-specific and lives only in
// its own period's columns.
const IDENTITY_FIELDS = ['productId', 'shopName', 'code', 'name', 'group1', 'group2', 'group3', 'salesGroup', 'dateTime']

// Renames every field of a comparison-period row to its `${field}_compare` form.
const toCompareShape = row => Object.keys(row).reduce((acc, field) => {
	acc[`${field}_compare`] = row[field]
	return acc
}, {})

const keyOf = (row, matchKeys) => matchKeys.map(key => row[key]).join(' ')

/**
 * @param {Array<object>} primaryInput   rows for the selected (primary) period
 * @param {Array<object>} comparisonInput rows for the comparison period
 * @param {string[]} matchKeys           identity keys for the active grouping
 * @returns {Array<object>|null} merged rows, or null when both periods are empty
 */
export default function mergeAbcComparison(primaryInput, comparisonInput, matchKeys = ['productId']) {
	const primary = Array.isArray(primaryInput) ? primaryInput : []
	const comparison = Array.isArray(comparisonInput) ? comparisonInput : []

	if (!primary.length && !comparison.length) {
		return null
	}
	if (!comparison.length) {
		return primary
	}

	// Index comparison rows by key (first row wins for a duplicate key).
	const comparisonByKey = new Map()
	comparison.forEach(row => {
		const key = keyOf(row, matchKeys)
		if (!comparisonByKey.has(key)) {
			comparisonByKey.set(key, row)
		}
	})

	// Start from the primary period; attach the matching comparison row (once).
	const consumedKeys = new Set()
	const merged = primary.map(row => {
		const key = keyOf(row, matchKeys)
		const comparisonRow = consumedKeys.has(key) ? null : comparisonByKey.get(key)
		consumedKeys.add(key)
		return comparisonRow ? { ...row, ...toCompareShape(comparisonRow) } : { ...row }
	})

	// Append comparison-only items: identity fields in the base columns so the
	// item is listed, measures only in the `_compare` columns.
	comparison.forEach(row => {
		const key = keyOf(row, matchKeys)
		if (consumedKeys.has(key)) {
			return
		}
		consumedKeys.add(key)
		const identity = IDENTITY_FIELDS.reduce((acc, field) => {
			if (Object.prototype.hasOwnProperty.call(row, field)) {
				acc[field] = row[field]
			}
			return acc
		}, {})
		merged.push({ ...identity, ...toCompareShape(row) })
	})

	return merged
}
